import { auth } from "../components/firebase";
import { BASE_URL } from "../integrations/APIClient";

const ENABLED_KEY = "hoodTtsEnabled";
const VOICE_KEY = "hoodTtsVoice";
const DEFAULT_VOICE = "nova";
const DEFAULT_RATE = 1.12;
const CACHE_LIMIT = 18;

export const WEB_HOOD_VOICES = [
  { id: "nova", label: "Nova", description: "Soft female · light and friendly" },
  { id: "shimmer", label: "Shimmer", description: "Gentle female · bright and soft" },
  { id: "coral", label: "Coral", description: "Warm female · clear and friendly" },
  { id: "marin", label: "Marin", description: "Natural female · calm adult" },
  { id: "sage", label: "Sage", description: "Calm · mature and steady" },
  { id: "alloy", label: "Alloy", description: "Neutral · balanced" },
];

let activeAudio = null;
let sharedAudio = null;
let activeObjectUrl = "";
let playbackToken = 0;
let unlockInstalled = false;
const audioCache = new Map();

const SILENT_WAV =
  "data:audio/wav;base64,UklGRqQCAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YYACAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA";

function ensureSharedAudio() {
  if (typeof window === "undefined") return null;
  if (!sharedAudio) {
    sharedAudio = new Audio();
    sharedAudio.preload = "auto";
    sharedAudio.setAttribute("playsinline", "");
  }
  return sharedAudio;
}

function installAudioUnlock() {
  if (unlockInstalled || typeof window === "undefined") return;
  unlockInstalled = true;
  const unlock = async () => {
    const audio = ensureSharedAudio();
    if (!audio) return;
    try {
      audio.src = SILENT_WAV;
      audio.volume = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 1;
    } catch {}
    window.removeEventListener("pointerdown", unlock, true);
    window.removeEventListener("touchstart", unlock, true);
    window.removeEventListener("keydown", unlock, true);
  };
  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("touchstart", unlock, true);
  window.addEventListener("keydown", unlock, true);
}

if (typeof window !== "undefined") installAudioUnlock();

function normalizeLang(lang) {
  const value = String(lang || "en").toLowerCase();
  if (value.startsWith("pl")) return "pl";
  if (value.startsWith("es")) return "es";
  return "en";
}

function browserLocale(lang) {
  const value = normalizeLang(lang);
  if (value === "pl") return "pl-PL";
  if (value === "es") return "es-ES";
  return "en-GB";
}

function cleanForSpeech(text) {
  return String(text || "")
    .replace(/\[\[HOOD_ACTION:[^\]]+\]\]/gi, "")
    .replace(/\b\d+\.{1,3}\s*(?=[KQRBNO0a-h])/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function voiceExists(id) {
  return WEB_HOOD_VOICES.some((voice) => voice.id === id);
}

function cacheKey(text, lang, voice) {
  return `${voice}|${normalizeLang(lang)}|${text}`;
}

function rememberAudio(key, value) {
  if (audioCache.has(key)) audioCache.delete(key);
  audioCache.set(key, value);
  while (audioCache.size > CACHE_LIMIT) {
    const first = audioCache.keys().next().value;
    audioCache.delete(first);
  }
}

function decodeAudioBase64(audioBase64, mimeType = "audio/mpeg") {
  const binary = atob(audioBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: mimeType || "audio/mpeg" });
}

async function requestRemoteSpeech(text, lang, voice, signal) {
  const current = auth.currentUser;
  if (!current) throw new Error("login_required");
  const token = await current.getIdToken();
  const response = await fetch(`${BASE_URL}/api/tts/hood`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      text,
      lang: normalizeLang(lang),
      voice,
      model: "gpt-4o-mini-tts",
      format: "mp3",
    }),
    signal,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.audioBase64) {
    const error = new Error(data?.error || data?.message || `tts_${response.status}`);
    error.status = response.status;
    throw error;
  }
  const blob = decodeAudioBase64(data.audioBase64, data.mimeType || "audio/mpeg");
  return { blob, voice: data.voice || voice, mimeType: data.mimeType || "audio/mpeg" };
}

function fallbackBrowserSpeech(text, lang, onEnd) {
  if (typeof window === "undefined" || !window.speechSynthesis || typeof SpeechSynthesisUtterance === "undefined") {
    onEnd?.();
    return false;
  }
  try {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = browserLocale(lang);
    utterance.rate = 1.08;
    utterance.pitch = 1.03;
    const voices = window.speechSynthesis.getVoices?.() || [];
    const base = normalizeLang(lang);
    const sameLanguage = voices.filter((item) => String(item.lang || "").toLowerCase().startsWith(base));
    const preferred = sameLanguage.find((item) =>
      /(samantha|serena|zosia|ewa|paulina|monica|female|google)/i.test(item.name || "")
    ) || sameLanguage[0];
    if (preferred) utterance.voice = preferred;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      onEnd?.();
    };
    utterance.onend = finish;
    utterance.onerror = finish;
    window.speechSynthesis.speak(utterance);
    return true;
  } catch {
    onEnd?.();
    return false;
  }
}

export function getWebHoodVoiceEnabled() {
  if (typeof window === "undefined") return true;
  try {
    const saved = window.localStorage.getItem(ENABLED_KEY);
    if (saved === null) return true;
    return saved !== "false" && saved !== "0";
  } catch {
    return true;
  }
}

export function setWebHoodVoiceEnabled(enabled) {
  const value = Boolean(enabled);
  if (typeof window !== "undefined") {
    try { window.localStorage.setItem(ENABLED_KEY, value ? "true" : "false"); } catch {}
  }
  if (!value) stopWebHoodVoice();
  try {
    window.dispatchEvent(new CustomEvent("chesshood:hood-voice", { detail: { enabled: value } }));
  } catch {}
  return value;
}

export function getWebHoodVoiceId() {
  if (typeof window === "undefined") return DEFAULT_VOICE;
  try {
    const saved = String(window.localStorage.getItem(VOICE_KEY) || "").trim().toLowerCase();
    return voiceExists(saved) ? saved : DEFAULT_VOICE;
  } catch {
    return DEFAULT_VOICE;
  }
}

export function setWebHoodVoiceId(voiceId) {
  const next = voiceExists(String(voiceId || "").toLowerCase())
    ? String(voiceId).toLowerCase()
    : DEFAULT_VOICE;
  if (typeof window !== "undefined") {
    try { window.localStorage.setItem(VOICE_KEY, next); } catch {}
    try {
      window.dispatchEvent(new CustomEvent("chesshood:hood-voice-choice", { detail: { voice: next } }));
    } catch {}
  }
  stopWebHoodVoice();
  return next;
}

export function stopWebHoodVoice() {
  playbackToken += 1;
  if (activeAudio) {
    try {
      activeAudio.pause();
      activeAudio.currentTime = 0;
    } catch {}
    activeAudio = null;
  }
  if (activeObjectUrl) {
    try { URL.revokeObjectURL(activeObjectUrl); } catch {}
    activeObjectUrl = "";
  }
  if (typeof window !== "undefined" && window.speechSynthesis) {
    try { window.speechSynthesis.cancel(); } catch {}
  }
}

export async function preloadWebHoodVoice(text, lang = "en", voiceId = getWebHoodVoiceId()) {
  const clean = cleanForSpeech(text);
  if (!clean || !getWebHoodVoiceEnabled()) return false;
  const voice = voiceExists(voiceId) ? voiceId : DEFAULT_VOICE;
  const key = cacheKey(clean, lang, voice);
  if (audioCache.has(key)) return true;
  try {
    const result = await requestRemoteSpeech(clean, lang, voice);
    rememberAudio(key, result);
    return true;
  } catch {
    return false;
  }
}

export function speakWebHoodVoice(
  text,
  lang = "en",
  { onEnd, rate = DEFAULT_RATE, voiceId = getWebHoodVoiceId() } = {},
) {
  const clean = cleanForSpeech(text);
  if (!clean || !getWebHoodVoiceEnabled()) {
    onEnd?.();
    return false;
  }

  stopWebHoodVoice();
  const token = playbackToken;
  const voice = voiceExists(voiceId) ? voiceId : DEFAULT_VOICE;
  const key = cacheKey(clean, lang, voice);

  const finish = () => {
    if (token !== playbackToken) return;
    activeAudio = null;
    if (activeObjectUrl) {
      try { URL.revokeObjectURL(activeObjectUrl); } catch {}
      activeObjectUrl = "";
    }
    onEnd?.();
  };

  const playResult = async (result) => {
    if (token !== playbackToken || !getWebHoodVoiceEnabled()) return;
    const url = URL.createObjectURL(result.blob);
    activeObjectUrl = url;
    const audio = ensureSharedAudio();
    if (!audio) {
      fallbackBrowserSpeech(clean, lang, onEnd);
      return;
    }
    activeAudio = audio;
    audio.pause();
    audio.currentTime = 0;
    audio.src = url;
    audio.preload = "auto";
    audio.volume = 1;
    audio.playbackRate = Math.max(0.9, Math.min(1.35, Number(rate) || DEFAULT_RATE));
    try { audio.preservesPitch = true; } catch {}
    audio.onended = finish;
    audio.onerror = () => {
      finish();
      fallbackBrowserSpeech(clean, lang, onEnd);
    };
    try {
      await audio.play();
    } catch {
      finish();
      fallbackBrowserSpeech(clean, lang, onEnd);
    }
  };

  const cached = audioCache.get(key);
  if (cached) {
    playResult(cached);
    return true;
  }

  requestRemoteSpeech(clean, lang, voice)
    .then((result) => {
      if (token !== playbackToken) return;
      rememberAudio(key, result);
      return playResult(result);
    })
    .catch(() => {
      if (token !== playbackToken) return;
      fallbackBrowserSpeech(clean, lang, onEnd);
    });

  return true;
}

export const WEB_HOOD_DEFAULT_VOICE = DEFAULT_VOICE;
export const WEB_HOOD_VOICE_RATE = DEFAULT_RATE;
